import {
  faMinus,
  faPlus,
  faShieldCheck,
  faTrashCan,
} from '@fortawesome/pro-regular-svg-icons'
import { NumberInput, Box, Button, Flex, Text, Tooltip } from '@mantine/core'
import { clsx } from 'clsx'
import { useEffect, useState } from 'react'

import { useTranslation } from '@flecto/translations'
import { type CoreItemAvailability, type CoreProduct } from '@flecto/types'
import { AvatarProduct, Icon } from '@flecto/ui'
import useMediaQuery from '@flecto/ui/lib/utils/useMediaQuery'

import { TextProductPrice } from '../../../products'
import styles from './styles.module.css'

export type BundleModalItemRowProps = {
  product: CoreProduct & { quantity?: number }
  onChangeItemQuantity?: (product: CoreProduct & { quantity?: number }) => void
}

export type CoreItemAvailabilityLocal = CoreItemAvailability & {
  original_remaining_quantity: number
}

export function BundleModalItemRow({
  product,
  onChangeItemQuantity,
}: BundleModalItemRowProps) {
  const { t: tShared } = useTranslation('shared', {
    keyPrefix: 'shared',
  })
  const { isMobile } = useMediaQuery()
  const { t: tItems } = useTranslation('saas', { keyPrefix: 'items' })
  const { t } = useTranslation('saas', { keyPrefix: 'booking_items' })

  const [itemQuantityPicked, setItemQuantityPicked] = useState<number>(0)
  const availableQuantity =
    product.item_attributes?.quantity_available &&
    product.item_attributes.track_quantity
      ? product.item_attributes?.quantity_available - itemQuantityPicked
      : 0

  const handleValueChange = (value: string | number) => {
    const numValue = Number(value)
    const adjustedValue =
      availableQuantity && numValue > availableQuantity
        ? availableQuantity
        : numValue
    setItemQuantityPicked(adjustedValue)
  }

  useEffect(() => {
    onChangeItemQuantity?.({ ...product, quantity: itemQuantityPicked })
  }, [itemQuantityPicked])

  return (
    <Box
      className={clsx(styles.itemLine, {
        [styles.itemSelected]: itemQuantityPicked > 0,
      })}
    >
      <AvatarProduct product={product} />

      <Box>
        <Flex direction='column'>
          <Flex align='center'>
            <Text truncate>{product.title}</Text>
            {product?.insured && (
              <Tooltip
                label={t('tooltip_insurance')}
                multiline
                zIndex={11000}
                withinPortal
              >
                <Text span>
                  <Icon
                    icon={faShieldCheck}
                    size='xs'
                    color='green'
                    style={{ marginLeft: '6px' }}
                    withCircleBackground
                  />
                </Text>
              </Tooltip>
            )}
          </Flex>
        </Flex>
      </Box>

      <TextProductPrice
        product={product}
        c='dimmed'
        ta='right'
        mr='sm'
        fallbackText='-'
        className={clsx({
          [styles.noWrap]: isMobile,
        })}
      />

      <Text miw={80} ta='right' mr='sm' c='gray'>
        {!product.item_attributes?.track_quantity
          ? tItems('new_bundle_modal_item_unlimited')
          : `${availableQuantity} ${tShared('in_stock')}`}
      </Text>

      <Flex align='center' gap='sm'>
        <Button
          size='sm'
          variant='outline'
          px={0}
          h={30}
          w={30}
          disabled={itemQuantityPicked <= 0}
          onClick={() => setItemQuantityPicked((prev) => Math.max(prev - 1, 0))}
        >
          <Text span>
            <Icon icon={faMinus} size='sm' />
          </Text>
        </Button>

        <NumberInput
          id={`input-quantity-${product.id}`}
          w={50}
          size='sm'
          min={0}
          step={1}
          max={availableQuantity}
          placeholder='0'
          value={itemQuantityPicked}
          className={styles.inputQuantity}
          onChange={handleValueChange}
          disabled={
            availableQuantity === 0 &&
            !product?.item_attributes?.allow_overbooking &&
            product?.item_attributes?.track_quantity
          }
          hideControls
        />

        <Button
          size='sm'
          variant='outline'
          px={0}
          h={30}
          w={30}
          disabled={
            availableQuantity! <= 0 &&
            !product?.item_attributes?.allow_overbooking &&
            product?.item_attributes?.track_quantity
          }
          onClick={() => setItemQuantityPicked((prev) => Math.max(prev + 1, 0))}
        >
          <Icon icon={faPlus} size='sm' />
        </Button>
      </Flex>

      <Box>
        {itemQuantityPicked > 0 && (
          <Box
            w={20}
            style={{ float: 'right', cursor: 'pointer' }}
            onClick={() => setItemQuantityPicked(0)}
          >
            <Icon icon={faTrashCan} color='gray.7' />
          </Box>
        )}
      </Box>
    </Box>
  )
}
